import { memo, useMemo } from 'react';
import { Line } from '@react-three/drei';
import type { TeamShapeBand } from '@4cm/core/tactics/overlays';
import {
  TEAM_SHAPE_HOME_HEX,
  TEAM_SHAPE_AWAY_HEX,
} from '../tactics2d/TeamShapeLayer2D';

/**
 * 3D version of the Sky-MNF "horizontal lines" overlay. One drei
 * `<Line>` per band, lying flat on the pitch (Y=0.013, above Spacing
 * + half-spaces). `worldUnits` lineWidth so the stroke reads at TV
 * scale and foreshortens correctly under perspective.
 */
export interface TeamShapeLayer3DProps {
  bands: ReadonlyArray<TeamShapeBand>;
  opacity: number;
  showAway?: boolean;
}

const BAND_Y = 0.013;

function TeamShapeLayer3DInner({
  bands,
  opacity,
  showAway = true,
}: TeamShapeLayer3DProps) {
  const items = useMemo(() => {
    if (!bands || bands.length === 0) return [];
    return bands
      .filter((b) => showAway || b.team !== 'away')
      .map((band, idx) => ({
        key: idx,
        team: band.team,
        points: band.points.map(
          (p) => [p.x, BAND_Y, p.z] as [number, number, number],
        ),
      }));
  }, [bands, showAway]);

  if (items.length === 0) return null;
  return (
    <group raycast={() => undefined} renderOrder={4}>
      {items.map(({ key, team, points }) => (
        <Line
          key={key}
          points={points}
          color={team === 'away' ? TEAM_SHAPE_AWAY_HEX : TEAM_SHAPE_HOME_HEX}
          worldUnits
          // Heavier stroke so the back / mid / front band lines read
          // as one continuous team shape, not three separate sketches.
          lineWidth={0.55}
          transparent
          opacity={opacity}
          depthWrite={false}
        />
      ))}
    </group>
  );
}

export const TeamShapeLayer3D = memo(TeamShapeLayer3DInner);
